import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, combineLatest, filter, of, takeUntil } from 'rxjs';
import { AccountInfoActions } from '../../account-info-header/actions';
import { selectSubAccountId, selectUserContext } from '../../account-info-header/reducers';
import {
  initializePage
} from '../../shared/actions/shared.actions';
import {
  selectStandingAuthTableStartTransactionClicked,
  selectViewInContext
} from '../../shared/reducers';
import { navRouteConstants } from '../../shared/utilities/Constants';
import { loadCreateStandingAuthEAuthClients } from '../../setup-eAuthorization/actions/eAuthorization.actions';
import { SharedActions } from '../../shared/actions';
import { CoreService, ICurrentContext } from '@charlesschwab/sac-ui-core';
import { ContextType } from '@charlesschwab/as-ui-core';
import { selectAuthorizeCallFailed, selectAuthorizeCallSuccessful, selectHasViewMoveMoney } from '../../authorize/reducers';
import { ContextService } from 'projects/integration/src/app/context.service';
import { ValidateWireFlagsService } from '../../shared/services/validate-WireFlags.service';

@Component({
  standalone: false,
  selector: 'mm-standing-authorizations',
  templateUrl: './standing-authorizations.component.html',
  styleUrls: ['./standing-authorizations.component.scss'],
})
export class StandingAuthorizationsComponent implements OnInit, OnDestroy {
  destroy$: Subject<void> = new Subject();
  createSloaStep$: Observable<string>;
  cancelFormClicked$: Subject<boolean> = new Subject();
  accountInContext: boolean = false;
  hasViewMoneyAccess$: Observable<boolean>;
  authorizedFinished$: Observable<boolean>;
  authorizedFailed$: Observable<boolean>;
  currentContext: ICurrentContext = {
    contextId: 0,
    contextType: ContextType.Unknown,
    firmId: 0,
    firmName: '',
    title: '',
  };
  masterAccountInContext: boolean;
  userInContext: string;

  constructor(
    private store: Store,
    private router: Router,
    private coreService: CoreService,
    private cdr: ChangeDetectorRef,
    private contextService: ContextService,
    private validateWireFlagsService: ValidateWireFlagsService
  ) {}

  ngOnInit(): void {
    this.store.dispatch(initializePage());

    this.authorizedFinished$ = this.store.select(selectAuthorizeCallSuccessful);
    this.authorizedFailed$ = this.store.select(selectAuthorizeCallFailed);
    this.authorizedFinished$
      .pipe(takeUntil(this.destroy$))
      .subscribe((finished) => {
        if (finished) {
          this.hasViewMoneyAccess$ = this.store.pipe(
            select(selectHasViewMoveMoney)
          );
        } else {
          this.hasViewMoneyAccess$ = of(false);
        }
      });

    combineLatest([
      this.store.select(selectUserContext),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([userContext]) => {
        this.userInContext = userContext;
        this.cdr.detectChanges();
      });

    this.store
      .pipe(
        select(selectSubAccountId),
        filter((id) => !!id),
        takeUntil(this.destroy$)
      )
      .subscribe((id) => {
        if (id.length > 0 && this.userInContext !== ContextType.Master) {
          this.store.dispatch(
            AccountInfoActions.loadTransactions({ accountId: id })
          );
          this.store.dispatch(
            loadCreateStandingAuthEAuthClients({ accountId: id })
          );
        }
      });
    
    this.createSloaStep$ = this.store.select(selectViewInContext);

    this.store.dispatch(SharedActions.standingAuthComponentLoaded());


    this.store
      .select(selectStandingAuthTableStartTransactionClicked)
      .pipe(
        takeUntil(this.destroy$),
        filter((startTransactionClicked) => startTransactionClicked)
      )
      .subscribe((_startTransactionClicked) => {
        const currentUrl: string = window.location.href;
        if (this.contextService.isMonolithUrl(currentUrl)) { 
          this.router.navigate([navRouteConstants.createTransaction])
        }
        else
        {
          let encodedContext = decodeURIComponent(this.coreService.currentContext.encodedContext as string);
          this.router.navigate([navRouteConstants.createTransaction], { queryParams: { ec: encodedContext } });
        }
      });

    this.coreService.currentContextUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        if (context) {
          this.currentContext = context;
        }
        if (this.currentContext?.contextType === ContextType.Account 
          || (this.currentContext?.contextType === ContextType.PALAccount 
            && this.validateWireFlagsService.enablePalWireCreateTransaction())) {
          this.accountInContext = true;
          this.masterAccountInContext = false;
        } else if (
          this.coreService?.currentContext?.contextType === ContextType.Master
        ) {
          this.masterAccountInContext = true;
          this.accountInContext = false;
        } else {
          this.masterAccountInContext = false;
          this.accountInContext = false;
        }
        this.store.dispatch(SharedActions.standingAuthComponentLoaded());
      });
    if (
      this.coreService?.currentContext?.contextType === ContextType.Account
      || (this.coreService?.currentContext?.contextType === ContextType.PALAccount
            && this.validateWireFlagsService.enablePalWireCreateTransaction())
    ) {
      this.accountInContext = true;
      this.masterAccountInContext = false;
    } else if (
      this.coreService?.currentContext?.contextType === ContextType.Master
    ) {
      this.masterAccountInContext = true;
      this.accountInContext = false;
    } else {
      this.masterAccountInContext = false;
      this.accountInContext = false;
    }
  }

  get showMasterStatusView(): boolean {
    return this.userInContext === 'MASTER';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
}
